import { index, integer, pgTable, text, timestamp } from "drizzle-orm/pg-core";
import { nanoid } from "nanoid";
import { user } from "./auth";
import { branches } from "./branches";
import { modifierOptions, productVariants } from "./catalog";
import { customers } from "./customers";

export const orders = pgTable(
  "orders",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderNo: text("order_no").notNull().unique(),
    channel: text("channel", { enum: ["APP", "WALKIN"] }).notNull(),
    branchId: text("branch_id")
      .notNull()
      .references(() => branches.id, { onDelete: "restrict" }),
    customerId: text("customer_id").references(() => customers.id, {
      onDelete: "set null",
    }),
    status: text("status")
      .$type<
        | "DRAFT"
        | "CONFIRMED"
        | "PREPARING"
        | "READY"
        | "COMPLETED"
        | "CANCELLED"
      >()
      .notNull(),
    subtotal: integer("subtotal").notNull(),
    discountTotal: integer("discount_total").notNull().default(0),
    total: integer("total").notNull(),
    paidTotal: integer("paid_total").notNull().default(0),
    currency: text("currency").notNull().default("LAK"),
    pickupType: text("pickup_type", { enum: ["ASAP", "SLOT"] }).notNull(),
    pickupAt: timestamp("pickup_at", { withTimezone: true, mode: "date" }),
    etaMinutes: integer("eta_minutes"),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("orders_order_no_idx").on(t.orderNo),
    index("orders_branch_idx").on(t.branchId),
    index("orders_customer_idx").on(t.customerId),
    index("orders_status_idx").on(t.status),
    index("orders_created_idx").on(t.createdAt),
  ],
);

export const orderItems = pgTable(
  "order_items",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    variantId: text("variant_id")
      .notNull()
      .references(() => productVariants.id, { onDelete: "restrict" }),
    qty: integer("qty").notNull(),
    unitPrice: integer("unit_price").notNull(),
    lineTotal: integer("line_total").notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("order_items_order_idx").on(t.orderId),
    index("order_items_variant_idx").on(t.variantId),
  ],
);

export const orderItemModifiers = pgTable(
  "order_item_modifiers",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderItemId: text("order_item_id")
      .notNull()
      .references(() => orderItems.id, { onDelete: "cascade" }),
    modifierOptionId: text("modifier_option_id")
      .notNull()
      .references(() => modifierOptions.id, { onDelete: "restrict" }),
    priceDelta: integer("price_delta").notNull().default(0),
    qty: integer("qty").notNull().default(1),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [index("order_item_modifiers_order_item_idx").on(t.orderItemId)],
);

export const orderStatusEvents = pgTable(
  "order_status_events",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    fromStatus: text("from_status").notNull(),
    toStatus: text("to_status").notNull(),
    byUserId: text("by_user_id").references(() => user.id, {
      onDelete: "set null",
    }),
    at: timestamp("at", { withTimezone: true, mode: "date" }).notNull(),
  },
  (t) => [
    index("order_status_events_order_idx").on(t.orderId),
    index("order_status_events_at_idx").on(t.at),
  ],
);

export const orderPaymentLinks = pgTable(
  "order_payment_links",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    paymentId: text("payment_id").notNull(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    }).notNull(),
  },
  (t) => [
    index("order_payment_links_order_idx").on(t.orderId),
    index("order_payment_links_payment_idx").on(t.paymentId),
  ],
);

/** คำขอยกเลิกออเดอร์ (หลัง accept) — พนักงานขอ → BM อนุมัติ/ปฏิเสธ */
export const orderVoidRequests = pgTable(
  "order_void_requests",
  {
    id: text("id")
      .primaryKey()
      .$defaultFn(() => nanoid()),
    orderId: text("order_id")
      .notNull()
      .references(() => orders.id, { onDelete: "cascade" }),
    reason: text("reason").notNull(),
    status: text("status")
      .$type<"PENDING" | "APPROVED" | "REJECTED">()
      .notNull()
      .default("PENDING"),
    requestedBy: text("requested_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedBy: text("reviewed_by").references(() => user.id, {
      onDelete: "set null",
    }),
    reviewedAt: timestamp("reviewed_at", {
      withTimezone: true,
      mode: "date",
    }),
    createdAt: timestamp("created_at", {
      withTimezone: true,
      mode: "date",
    })
      .notNull()
      .$defaultFn(() => new Date()),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
      mode: "date",
    })
      .notNull()
      .$defaultFn(() => new Date()),
  },
  (t) => [
    index("order_void_requests_order_idx").on(t.orderId),
    index("order_void_requests_status_idx").on(t.status),
  ],
);
